import { notify, TableStaticActions, useModalMultiple, withModalConditionalRender } from '@billy/design-system'
import React, { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { Invoice } from '@features/invoicesLegacy/types/invoice'

import { queryClient } from '../../../../../../config/queryClient'
import { EmberRoute } from '../../../../../../enums/emberRoute'
import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { useSegment } from '../../../../../../hooks'
import { useEmberRouteToUrlGenerator } from '../../../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { APIError } from '../../../../../../utils'
import { InvoiceReconciliationModal as InvoiceReconciliationModalComponent } from '../../../../elements/InvoiceReconclliationModal/InvoiceReconciliationModal'
import { VoidInvoiceModal as VoidInvoiceModalComponent } from '../../../../elements/VoidInvoiceModal'
import { InvoicesTableStaticActionType } from '../../enums/InvoicesTableStaticActionType'
import { useVoidInvoice } from '../../hooks/useVoidInvoice'
import { InvoicesTableStaticActionItem } from '../../types/InvoicesTableStaticActionItem'
import { VoidCreditNoteModal } from '../VoidCreditNoteModal'

interface InvoicesTableStaticActionsProps {
  invoice: Invoice
  isCreditNote?: boolean
  items: InvoicesTableStaticActionItem[]
}

const VoidInvoiceModal = withModalConditionalRender(VoidInvoiceModalComponent)
const InvoiceReconciliationModal = withModalConditionalRender(InvoiceReconciliationModalComponent)

const getInvoiceReconciliationModalId = (id: string) => `${id}-invoice-reconciliation-static-action`
const getVoidInvoiceModalId = (id: string) => `${id}-void-invoice-modal`
const getVoidCreditNoteModalId = (id: string) => `${id}-void-credit-note-modal`

export const InvoicesTableStaticActions = ({
  invoice,
  isCreditNote,
  items,
}: InvoicesTableStaticActionsProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()

  const { generateUrl } = useEmberRouteToUrlGenerator()
  const navigate = useNavigate()

  const invoiceId = invoice.id

  const voidInvoiceModalId = getVoidInvoiceModalId(invoiceId)
  const voidCreditNoteModalId = getVoidCreditNoteModalId(invoiceId)
  const invoiceReconciliationModalId = getInvoiceReconciliationModalId(invoiceId)
  const { open: openModal, close: closeModal } = useModalMultiple([
    voidInvoiceModalId,
    voidCreditNoteModalId,
    invoiceReconciliationModalId,
  ])

  const { voidInvoice } = useVoidInvoice({
    onSuccess: () => {
      notify({
        id: NotificationKeys.InvoiceVoided,
        message: t('invoices.void_document.success'),
        variant: 'success',
      })
      closeModal(voidInvoiceModalId)
      queryClient.invalidateQueries(QueryKeys.Invoices)
    },
    onError: (error?: APIError) => {
      notify({
        id: NotificationKeys.InvoiceVoided,
        message: error?.message || t('error'),
        variant: 'error',
      })
    },
  })

  const handleNavigateToCreditNote = useCallback(() => {
    const url = generateUrl?.(EmberRoute.InvoiceCreditNote, invoiceId)
    if (url) {
      navigate(url)
    }
  }, [generateUrl, navigate, invoiceId])

  const handleVoidInvoice = useCallback(() => {
    voidInvoice(invoiceId)
  }, [voidInvoice, invoiceId])

  const handleStaticActionSelect = useCallback(
    (id: string, value: InvoicesTableStaticActionType) => {
      switch (value) {
        case InvoicesTableStaticActionType.Credit: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Invoices',
            button: InvoicesTableStaticActionType.Credit,
          })
          break
        }
        case InvoicesTableStaticActionType.Duplicate: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Invoices',
            button: InvoicesTableStaticActionType.Duplicate,
          })
          break
        }
        case InvoicesTableStaticActionType.RegisterPayment: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Invoices',
            button: InvoicesTableStaticActionType.RegisterPayment,
          })
          openModal(invoiceReconciliationModalId)
          break
        }
        case InvoicesTableStaticActionType.Void: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Invoices',
            button: InvoicesTableStaticActionType.Void,
          })
          if (isCreditNote) {
            openModal(voidCreditNoteModalId)
          } else {
            openModal(voidInvoiceModalId)
          }
          break
        }
      }
    },
    [invoiceReconciliationModalId, isCreditNote, openModal, track, voidCreditNoteModalId, voidInvoiceModalId],
  )

  return (
    <>
      <TableStaticActions items={items} onSelect={handleStaticActionSelect} />
      <VoidInvoiceModal
        id={voidInvoiceModalId}
        invoiceId={invoiceId}
        onInvoiceVoid={handleVoidInvoice}
        onNavigateToCreditNote={handleNavigateToCreditNote}
      />
      <VoidCreditNoteModal id={voidCreditNoteModalId} creditNoteId={invoiceId} />
      <InvoiceReconciliationModal subject={invoice} id={invoiceReconciliationModalId} />
    </>
  )
}
